import { useCallback } from 'react'

import { useNavigate } from 'react-router-dom'
import {
  type Notification,
  notificationsApi,
  useCloseNotificationOccurrenceMutation,
  useDenyAccessRequestMutation,
  useAppDispatch,
  useDismissNotificationMutation,
  useGrantAccessRequestMutation,
  useMarkNotificationsReadMutation,
  usePatchCustomAgentRunCompletionMutation
} from '@axilon-apps/domains-ontology-ui-state'
import type { NotificationAction, PresentedNotification } from './notification-presentation'

/** Router state the chat page reads once to seed a new conversation. */
export interface ChatPrefillState {
  chatPrefill: { draft: string; appContext: { key: string; value: string }[] }
}

/** The custom-agent run a finding is about. */
export function findingRunId(n: Notification): string | null {
  if (n.subject_type === 'custom_agent_run' && n.subject_id) return n.subject_id
  const runId = n.metadata?.run_id
  return typeof runId === 'string' && runId !== '' ? runId : null
}

function accessRequestId(n: Notification): string | null {
  if (n.subject_type === 'resource_access_request' && n.subject_id) return n.subject_id
  const requestId = n.metadata?.requestId
  return typeof requestId === 'string' && requestId !== '' ? requestId : null
}

export function useNotificationActions(onDone?: () => void) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [dismissNotification] = useDismissNotificationMutation()
  const [markRead] = useMarkNotificationsReadMutation()
  const [grantAccess] = useGrantAccessRequestMutation()
  const [denyAccess] = useDenyAccessRequestMutation()
  const [closeOccurrence] = useCloseNotificationOccurrenceMutation()
  const [patchRunCompletion] = usePatchCustomAgentRunCompletionMutation()

  const resolveAll = useCallback(
    async (members: Notification[]) => {
      await Promise.all(members.map((m) => dismissNotification(m.id).unwrap()))
    },
    [dismissNotification]
  )

  // The proxy closes rows when their subject is handled (verdict recorded,
  // request decided); refetch so the bell reflects it.
  const refresh = useCallback(() => {
    dispatch(
      notificationsApi.util.invalidateTags([
        { type: 'Notification', id: 'LIST' },
        'NotificationCounts'
      ])
    )
  }, [dispatch])

  const markOpened = useCallback(
    (item: PresentedNotification) => {
      const unread = item.members.filter((m) => m.read === false).map((m) => m.id)
      if (unread.length) void markRead({ ids: unread })
    },
    [markRead]
  )

  const open = useCallback(
    (item: PresentedNotification) => {
      const href = item.presentation.href
      if (!href) return
      markOpened(item)
      onDone?.()
      navigate(href)
    },
    [markOpened, navigate, onDone]
  )

  const run = useCallback(
    async (item: PresentedNotification, action: NotificationAction) => {
      const n = item.notification
      if (action.kind === 'navigate') {
        markOpened(item)
        onDone?.()
        navigate(action.href)
        return
      }
      if (action.kind === 'discuss') {
        markOpened(item)
        onDone?.()
        const state: ChatPrefillState = {
          chatPrefill: { draft: action.prompt, appContext: action.appContext }
        }
        navigate('/chat', { state })
        return
      }
      switch (action.mutation) {
        case 'resolve_problem_occurrence': {
          const occurrenceId = n.subject_type === 'problem_occurrence' ? n.subject_id : undefined
          const version = n.metadata?.occurrence_version
          const taskVersion = n.metadata?.task_set_version
          if (!occurrenceId || typeof version !== 'number' || typeof taskVersion !== 'number')
            return
          await closeOccurrence({
            occurrenceId,
            operationKey: `notification:${n.id}:${version}:${taskVersion}`,
            expectedVersion: version,
            expectedTaskSetVersion: taskVersion
          }).unwrap()
          refresh()
          return
        }
        case 'resolve_agent_run': {
          const runId = findingRunId(n)
          if (runId) {
            await patchRunCompletion({ runId, status: 'gone' }).unwrap()
            refresh()
          } else {
            await resolveAll(item.members)
          }
          return
        }
        case 'grant_access_viewer':
        case 'grant_access_editor': {
          const requestId = accessRequestId(n)
          if (!requestId) return
          await grantAccess({
            requestId,
            accessLevel: action.mutation === 'grant_access_editor' ? 'editor' : 'viewer'
          }).unwrap()
          refresh()
          return
        }
        case 'deny_access': {
          const requestId = accessRequestId(n)
          if (requestId) {
            await denyAccess({ requestId }).unwrap()
            refresh()
          }
          return
        }
        case 'dismiss':
          await resolveAll(item.members)
          return
      }
    },
    [
      closeOccurrence,
      denyAccess,
      grantAccess,
      markOpened,
      navigate,
      onDone,
      patchRunCompletion,
      refresh,
      resolveAll
    ]
  )

  return { open, run, resolveAll, markOpened }
}
